// @flow
import * as React from 'react';
import DayPicker from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import MomentLocaleUtils from 'react-day-picker/moment';
import moment from 'moment';
import styled, { withTheme } from 'styled-components';
import WeekDay from './WeekDay';
import DatePickerHeader from './DatePickerHeader';
import { Modifiers } from './DatePickerDayModifier';

export const PICKER_ID = 'statsradio-day-picker';

const DAY_PADDING = '8px !important';
const Css = styled.div`
  .DayPicker-wrapper:focus,
  .DayPicker-Day:focus {
    outline: 0;
  }

  .DayPicker-Body {
    color: ${props => props.theme.V3.colors.text.tertiary};
    font-size: 12.6px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: normal;
  }

  .DayPicker-Weekday {
    color: #000;
    padding: 0;
    text-transform: capitalize;
    font-size: 12px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: normal;
  },

  .DayPicker-Day {
    color: ${props => props.theme.V3.colors.text.emphasis};
    padding: ${DAY_PADDING};

    :hover {
      :not(.DayPicker--interactionDisabled).DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside) {
        background-color: ${props => props.theme.V3.colors.primary.light};
        color: ${props => props.theme.V3.colors.text.tertiary};
      }
    }

    .DayPicker-Day--outside {
      color: #AAA;
      cursor: pointer;
    }

    .DayPicker-Day--disabled {
      color: #AAA;
      cursor: default !important;
    }

    .DayPicker-Day--selected{
      :not(.DayPickerDay--inRange):hover {
        background-color: ${props => props.theme.V3.colors.primary.normal};
        color: ${props => props.theme.V3.colors.text.tertiary};
      }

      :not(.DayPickerDay--inRange) {
        background-color: ${props => props.theme.V3.colors.primary.normal};
        color: ${props => props.theme.V3.colors.text.tertiary};
        font-weight: bold;
      }
    }
    
    .DayPicker-Day--inRange:not(.DayPicker-Day--outside) {
      background-color: #ecf7f7 !important;
      border-radius: 0;
      color: ${props => props.theme.V3.colors.text.emphasis} !important;
      font-weight: normal !important;
    }

    .DayPicker-Day--inRange.DayPicker-Day--outside {
      background-color: #ecf7f7 !important;
      border-radius: 0;
      color: #AAA !important;
      font-weight: normal !important;

      :not(.DayPicker-Day--interactionDisabled) {

      }

      :not(.DayPicker-Day--interactionDisabled):hover {
        background-color: ${props => props.theme.V3.colors.primary.light};
        color: ${props => props.theme.V3.colors.text.tertiary};
      }
    }

    .DayPicker-Day--start {
      border-top-left-radius: 50% !important;
      border-bottom-left-radius: 50% !important;
      padding: 0 !important;
    }

    .DayPicker-Day--end {
      border-top-right-radius: 50% !important;
      border-bottom-right-radius: 50% !important;
      padding: 0 !important;
    }

    .DayPicker-Day--edge {
      padding: ${DAY_PADDING};
      border-radius: 50% !important;
      background-color: ${props => props.theme.V3.colors.primary.normal} !important;
      color: ${props => props.theme.V3.colors.text.tertiary};
      font-weight: bold !important;
    }

    .DayPicker-Day--today {
      font-weight: bold !important;
    }

    .DayPicker-Day--interactionDisabled:not(.DayPicker-Day--today):not(.DayPicker-Day--outside):not(.DayPicker-Day--disabled) {
      color: ${props => props.theme.V3.colors.text.emphasis} !important;
      cursor: default !important;

      :hover {
        background-color: transparent !important;
        font-weight: normal !important;
      }
    }
  }

  .DayPicker-Month {
    margin-top: 0;
  }
`;

function NoCaption() {
  return null;
}

interface Props {
  disabledDates: any;
  date: moment;
  maxDate: moment | void;
  minDate: moment | void;
  modifiers?: Modifiers;
  onDayClick: (day: Date, modifiers: Modifiers) => void;
  onDayMouseEnter?: (day: Date, modifiers: Modifiers) => void;
  onDayMouseLeave?: () => void;
  renderDay: (day: Date, modifiers: Modifiers) => React.Element<*> | number;
  selectedDays: any[];
}
export class DatePicker extends React.Component<Props> {
  static defaultProps = {
    date: moment(),
    disabledDates: {
      after: undefined,
      before: undefined,
    },
  }

  getDisabledDates = () => {
    const { disabledDates, minDate, maxDate } = this.props;
    const disabled = {};

    minDate && (disabled.before = minDate.toDate());
    maxDate && (disabled.after = maxDate.toDate());

    disabledDates.before && (disabled.before = disabledDates.before);
    disabledDates.after && (disabled.after = disabledDates.after);

    return disabled;
  }

  render() {
    const { minDate, maxDate, selectedDays, modifiers, onDayClick, onDayMouseEnter, onDayMouseLeave, renderDay, date } = this.props;
    return (
      <Css>
        <DayPicker
          id={PICKER_ID}
          captionElement={NoCaption}
          disabledDays={this.getDisabledDates()}
          firstDayOfWeek={moment().startOf('isoWeek').day()}
          fixed
          fromMonth={minDate ? minDate.toDate() : undefined}
          locale={moment.locale()}
          localeUtils={MomentLocaleUtils}
          modifiers={modifiers}
          month={date.toDate()}
          navbarElement={DatePickerHeader}
          onDayClick={onDayClick}
          onDayMouseEnter={onDayMouseEnter}
          onDayMouseLeave={onDayMouseLeave}
          renderDay={renderDay}
          selectedDays={selectedDays}
          showOutsideDays
          toMonth={maxDate ? maxDate.toDate() : undefined}
          weekdayElement={WeekDay}
        />
      </Css>
    );
  }
}

export default withTheme(DatePicker);



// WEBPACK FOOTER //
// ./src/common/components/ui/datePicker/inner/DatePicker.js